import { FILE_SIZES } from "common/constants";

export const UPLOAD_ABORTED = "aborted";
export const NO_FILE_SELECTED = "No file selected";

/**
 * A function for programatically prompting a user to provide a file from their local file system. It
 * produces an input of type "file" and programatically clicks it.
 *
 * @param mimeTypesAccepted The MIME types allowed
 * @param asDataURL Determines whether the internal FileReader will use `readAsDataURL` or `readAsText`.
 * @param sizeLimit A size limit in bytes
 * @returns A Promise that completes when the content is read where the return value includes the actual body of the file as a string,
 * along with `sizeBytes` taken from the `File` itself, which is exact and costs nothing to read
 */
export async function uploadFile(
  mimeTypesAccepted: string = "application/json,.json",
  asDataURL: boolean = false,
  sizeLimit?: number,
): Promise<{ body: string; filename: string; sizeBytes: number }> {
  const inputEl = document.createElement("input") as HTMLInputElement;
  inputEl.type = "file";
  inputEl.accept = mimeTypesAccepted;
  document.body.appendChild(inputEl);

  return new Promise((resolve, reject) => {
    let settled = false;

    const cleanup = () => {
      inputEl.removeEventListener("change", handleChange);
      inputEl.removeEventListener("cancel", handleCancel);

      if (inputEl.parentNode) {
        inputEl.parentNode.removeChild(inputEl);
      }
    };

    const settle = (callback: () => void) => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      callback();
    };

    const rejectWith = (message: string) => {
      settle(() => {
        reject(message);
      });
    };

    const resolveWith = (payload: { body: string; filename: string; sizeBytes: number }) => {
      settle(() => {
        resolve(payload);
      });
    };

    const handleChange = () => {
      const { files } = inputEl;
      if (!files || files.length === 0) {
        rejectWith(NO_FILE_SELECTED);
        return;
      }

      const file = files[0];
      if (!file) {
        rejectWith(NO_FILE_SELECTED);
        return;
      }

      if (sizeLimit && file.size > sizeLimit) {
        rejectWith(`File size must be less than ${sizeLimit / FILE_SIZES.MB}MB.`);
        return;
      }

      const reader = new FileReader();
      reader.onloadend = (event) => {
        const loaded = typeof event.loaded === "number" ? event.loaded : 0;
        if (loaded === 0 && reader.result === null) {
          return;
        }

        resolveWith({ body: reader.result as string, filename: file.name, sizeBytes: file.size });
      };

      if (asDataURL) {
        reader.readAsDataURL(file);
      } else {
        reader.readAsText(file);
      }
    };

    const handleCancel = () => {
      rejectWith(UPLOAD_ABORTED);
    };

    inputEl.addEventListener("change", handleChange);
    inputEl.addEventListener("cancel", handleCancel);

    inputEl.click();
  });
}

/**
 * A function for programmatically prompting a user to provide a file from their local file system. It
 * produces an input of type "file" and programmatically clicks it.
 *
 * @param mimeTypesAccepted The MIME types allowed
 * @param sizeLimit A size limit in bytes
 * @returns A Promise for a File that can be read however the caller wishes
 */
export async function uploadFileNoRead(
  mimeTypesAccepted: string = "application/json,.json",
  sizeLimit?: number,
): Promise<File> {
  const inputEl = document.createElement("input") as HTMLInputElement;
  inputEl.type = "file";
  inputEl.accept = mimeTypesAccepted;
  document.body.appendChild(inputEl);

  return new Promise((resolve, reject) => {
    let settled = false;

    const cleanup = () => {
      inputEl.removeEventListener("change", handleChange);
      inputEl.removeEventListener("cancel", handleCancel);

      if (inputEl.parentNode) {
        inputEl.parentNode.removeChild(inputEl);
      }
    };

    const settle = (callback: () => void) => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      callback();
    };

    const rejectWith = (message: string) => {
      settle(() => {
        reject(message);
      });
    };

    const resolveWith = (file: File) => {
      settle(() => {
        resolve(file);
      });
    };

    const handleChange = () => {
      const { files } = inputEl;
      if (!files || files.length === 0) {
        rejectWith(NO_FILE_SELECTED);
        return;
      }

      const file = files[0];
      if (!file) {
        rejectWith(NO_FILE_SELECTED);
        return;
      }

      if (sizeLimit && file.size > sizeLimit) {
        rejectWith(`File size must be less than ${sizeLimit / FILE_SIZES.MB}MB.`);
        return;
      }

      resolveWith(file);
    };

    const handleCancel = () => {
      rejectWith(UPLOAD_ABORTED);
    };

    inputEl.addEventListener("change", handleChange);
    inputEl.addEventListener("cancel", handleCancel);

    inputEl.click();
  });
}
